import React, { FC, useEffect, useMemo, useState }  from 'react';
import { Button }                                   from 'semantic-ui-react';
import { Flexbox, Page, PageTitleContainer }        from '@src/views/containers';
import { StyledContainer }                          from '@src/views/containers';
import { FlexAlign, FlexJustify }                   from '@src/types';
import { useSystemModals }                          from '@src/hooks';
import { SideMenuContentResponse, useCentralCalls } from '@src/rest/CentralCalls';
import ConsultantDashSidebar                        from './ConsultantDashSidebar';
import AIWindow                                     from './AIWindow';
import type { AIWindowMode }                        from './AIWindow';
import ClientContent                                from './ClientContent';
import ClientDashboard                              from './ClientDashboard';
import ConsultantScopedDashboard                    from './ConsultantScopedDashboard';
import FinanceHourReports                           from './FinanceHourReports';
import AddSowDialog                                 from './AddSowDialog';
import ClientOfferingDialog                         from './ClientOfferingDialog';
import ConsultantAdminControls                      from './ConsultantAdminControls';
import HeaderTimeClock                              from './HeaderTimeClock';
import type { EditSowPayload }                      from './ClientOutlineTypes';
import {
	Company,
	companyFromDbClient,
	Consultant,
	CONSULTANT_DASH_VIEWS,
	ConsultantDashView,
	consultantFromDbRow,
	DashSegment,
	getSidebarSections,
	isConsultantDashView,
	SidebarEntity,
} from './consultantDashModel';
import {
	readStoredJson,
	readStoredString,
	writeStoredJson,
	writeStoredString,
} from './consultantDashStorage';
import './ConsultantDash.scss';

const VIEW_STORAGE_KEY = 'corval-consultant-page-view';
const PORTFOLIO_PAGE_STORAGE_KEY = 'corval-consultant-page-portfolio-page-id';
const hiddenEntitiesKey = ( segment: DashSegment ) => `corval-consultant-page-hidden-entities-${ segment }`;

const readStoredView = (): ConsultantDashView => {
	const stored = readStoredString( VIEW_STORAGE_KEY, '' );
	return isConsultantDashView( stored ) ? stored : 'workspace';
};

const ConsultantPage: FC = () => {
	const { executeStoredProcedure, getConsultantId } = useCentralCalls();
	const modalActions = useSystemModals();

	// The logged-in user's tng.Consultant id (resolved server-side from their account); undefined if they have none.
	const [ currentConsultantId, setCurrentConsultantId ] = useState<string | undefined>( undefined );

	useEffect( () => {
		let cancelled = false;

		( async () => {
			try {
				const consultant = await getConsultantId();
				if( !cancelled ) setCurrentConsultantId( consultant?.id || undefined );
			} catch( e ) {
				console.error( 'Failed to load the current consultant', e );
			}
		} )();

		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [] );

	const [ companies, setCompanies ]       = useState<Company[]>( [] ),
	      [ consultants, setConsultants ] = useState<Consultant[]>( [] );

	// View As is removed: the page is always the admin overview (every client and consultant).
	const segment: DashSegment = 'executive';

	const hydrateSideMenuContent = async ( content: SideMenuContentResponse ) => {
		// Always replace the client list so deleting the last client empties the menu.
		setCompanies( ( content.clients ?? [] ).map( companyFromDbClient ) );

		if( content.consultants?.length ){
			setConsultants( content.consultants.map( consultantFromDbRow ) );
		}
	};

	const reloadSideMenuContent = async () => {
		const content = await executeStoredProcedure( 'tng.SideMenuContent', {} ) as SideMenuContentResponse;
		await hydrateSideMenuContent( content );
	};

	useEffect( () => {
		let cancelled = false;

		const loadSideMenuContent = async () => {
			try {
				const content = await executeStoredProcedure( 'tng.SideMenuContent', {} ) as SideMenuContentResponse;
				if( cancelled ) return;

				await hydrateSideMenuContent( content );
			} catch {
				// Leave the lists empty if the API is unavailable.
			}
		};

		loadSideMenuContent();

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [] );

	const sections = useMemo(
		() => getSidebarSections( segment, undefined, companies, consultants ),
		[ segment, companies, consultants ]
	);

	const allEntities = useMemo(
		() => sections.reduce<SidebarEntity[]>( ( acc, section ) => acc.concat( section.entities ), [] ),
		[ sections ]
	);

	const [ hiddenEntityIds, setHiddenEntityIds ] = useState<string[]>(
		() => readStoredJson<string[]>( hiddenEntitiesKey( segment ), [] )
	);

	const visibleEntities = useMemo(
		() => allEntities.filter( entity => entity.kind === 'reports' || !hiddenEntityIds.includes( entity.id ) ),
		[ allEntities, hiddenEntityIds ]
	);

	const [ activeEntity, setActiveEntity ]         = useState<SidebarEntity | null>( null ),
	      [ sidebarCollapsed, setSidebarCollapsed ] = useState( false ),
	      [ aiWindowMode, setAiWindowMode ] = useState<AIWindowMode>( 'collapsed' ),
	      [ activeView, setActiveView ]             = useState<ConsultantDashView>( readStoredView );

	const showReports = activeEntity?.kind === 'reports';
	const showFinance = activeEntity?.kind === 'finance';

	useEffect( () => {
		setHiddenEntityIds( readStoredJson<string[]>( hiddenEntitiesKey( segment ), [] ) );
	}, [ segment ] );

	useEffect( () => {
		setActiveEntity( prev => {
			// Finance is hard-coded in the sidebar, so it is never part of visibleEntities.
			if( prev?.kind === 'finance' ) return prev;
			if( prev && visibleEntities.some( entity => entity.id === prev.id ) ) return prev;
			return visibleEntities.find( entity => entity.kind === 'company' ) ?? visibleEntities[ 0 ] ?? null;
		} );
	}, [ visibleEntities ] );

	const handleViewChange = ( view: ConsultantDashView ) => {
		setActiveView( view );
		writeStoredString( VIEW_STORAGE_KEY, view );
	};

	const handleToggleEntityVisibility = ( entityId: string ) => {
		const nextHidden = hiddenEntityIds.includes( entityId )
			? hiddenEntityIds.filter( id => id !== entityId )
			: [ ...hiddenEntityIds, entityId ];

		setHiddenEntityIds( nextHidden );
		writeStoredJson( hiddenEntitiesKey( segment ), nextHidden );

		if( activeEntity?.id === entityId && nextHidden.includes( entityId ) ){
			const fallback = allEntities.find( entity => !nextHidden.includes( entity.id ) ) ?? null;
			setActiveEntity( fallback );
		}
	};

	const selectedClientId =
		activeEntity?.kind === 'company' ? activeEntity.id : undefined;

	const selectedClientName = useMemo( () => {
		if( !selectedClientId ) return 'Client';
		return companies.find( c => c.id === selectedClientId )?.name || activeEntity?.name || 'Client';
	}, [ selectedClientId, companies, activeEntity?.name ] );

	const handleOpenAddSowDialog = (
		clientIdToUse: string,
		clientNameToUse: string,
		onSaved?: () => void,
		editSow?: EditSowPayload
	) => {
		modalActions.show(
			<AddSowDialog
				clientId={ clientIdToUse }
				clientName={ clientNameToUse }
				onSaved={ onSaved }
				editSow={ editSow }
				onClose={ () => modalActions.toggle( false ) }
			/>,
			{ size: 'large', centered: false }
		);
	};

	const handleOpenAddOfferingDialog = ( clientIdToUse: string, sowId: string, sowName: string, onSaved?: () => void ) => {
		modalActions.show(
			<ClientOfferingDialog
				clientId={ clientIdToUse }
				sowId={ sowId }
				sowName={ sowName }
				onSaved={ onSaved }
				onClose={ () => modalActions.toggle( false ) }
			/>,
			{ size: 'small', centered: true }
		);
	};

	const handleOpenAdminControls = () => {
		modalActions.show(
			<ConsultantAdminControls
				onClose={ () => modalActions.toggle( false ) }
				onClientCreated={ reloadSideMenuContent }
			/>,
			{ size: 'small', centered: true }
		);
	};

	return (
		<Page
			name={ 'consultant-page' }
			padMainHorizontal={ false }
			padMainVertical={ false }
			allowContentScroll
			className={ 'consultantDashPage' }
		>
			<PageTitleContainer
				title={ 'Consultant' }
				description={ 'Consultant workspace.' }
				fluid
				alignContent={ FlexAlign.CENTER }
				hideOnMinimization
				rightFlexItemProps={ { className: 'text-right' } }
			>
				<div className={ 'consultant-view-as' }>
					<HeaderTimeClock />
				</div>
			</PageTitleContainer>

			<div className={ 'consultant-dash-layout' }>
				<div className={ `consultant-dash-sidebar ${ sidebarCollapsed ? 'collapsed' : '' }` }>
					<ConsultantDashSidebar
						sections={ sections }
						activeEntityId={ activeEntity?.id ?? '' }
						onSelectEntity={ setActiveEntity }
						hiddenEntityIds={ hiddenEntityIds }
						onToggleEntityVisibility={ handleToggleEntityVisibility }
						isCollapsed={ sidebarCollapsed }
						toggleCollapsed={ () => setSidebarCollapsed( prev => !prev ) }
						segment={ segment }
						onOpenAdminControls={ handleOpenAdminControls }
					/>
				</div>

				<div className={ 'consultant-dash-content' }>
					{ showFinance ? (
						<FinanceHourReports clients={ companies } consultants={ consultants }/>
					) : showReports ? (
						<ConsultantScopedDashboard
							scope={ 'consultantPortfolio' }
							params={ {} }
							title={ 'Reports' }
							pageStorageKey={ PORTFOLIO_PAGE_STORAGE_KEY }
							gridIdentityKey={ 'executive' }
							emptyMessage={ 'No report pages have been set up yet.' }
						/>
					) : selectedClientId ? (
						<>
							<Flexbox justify={ FlexJustify.START } align={ FlexAlign.CENTER } className={ 'consultant-view-switcher' }>
								<Button.Group size={ 'small' }>
									{ CONSULTANT_DASH_VIEWS.map( view => (
										<Button
											key={ view.value }
											type={ 'button' }
											basic={ activeView !== view.value }
											color={ 'purple' }
											icon={ view.icon }
											content={ view.label }
											aria-pressed={ activeView === view.value }
											onClick={ () => handleViewChange( view.value ) }
										/>
									) ) }
								</Button.Group>
							</Flexbox>

							{/* Kept mounted while hidden so the selected SOW, practice and offering survive a trip to the dashboard. */}
							<StyledContainer hidden={ activeView !== 'workspace' }>
								<ClientContent
									clientId={ selectedClientId }
									clientName={ selectedClientName }
									onOpenAddSowDialog={ ( clientIdToUse, clientNameToUse, onSaved, editSow ) =>
										handleOpenAddSowDialog( clientIdToUse, clientNameToUse, onSaved, editSow )
									}
									onOpenAddOfferingDialog={ handleOpenAddOfferingDialog }
									onClientDeleted={ reloadSideMenuContent }
								/>
							</StyledContainer>

							{ activeView === 'dashboard' && (
								<ClientDashboard clientId={ selectedClientId } clientName={ selectedClientName }/>
							) }
						</>
					) : (
						<p className={ 'consultant-widget-empty consultant-dash-empty-state' }>
							Select a client from the side menu.
						</p>
					) }
				</div>

				 <div className={ `consultant-dash-ai-window ${ aiWindowMode }` }>
					<AIWindow
						clientId={ selectedClientId }
						consultantId={ currentConsultantId }
						mode={ aiWindowMode }
						toggleCollapsed={ () => setAiWindowMode( prev => ( prev === 'collapsed' ? 'open' : 'collapsed' ) ) }
						toggleExtended={ () => setAiWindowMode( prev => ( prev === 'extended' ? 'open' : 'extended' ) ) }
					/>
				</div> 
			</div>
		</Page>
	);
};

export default ConsultantPage;
